import { request } from './client'

/* ============================================================================
   W-127 — LOADING THE HOTEL'S OWN ROOMS.

   TWO CALLS AND ONE FILE. The preview changes nothing and returns exactly what the
   commit would do; the commit re-reads the same file and applies it. A load of five
   hundred rooms cannot be undone, so the only safe order is look, then act — and the
   client's part of that is simply to send the same bytes twice rather than to remember
   a plan the server would then have to trust it about.
   ========================================================================= */

/** What loading a row would do. The server's word, not ours. */
export type LoadChange = 'NEW' | 'CHANGED' | 'UNCHANGED' | 'LEFT_OUT_OF_USE'

/**
 * Something wrong with the file, against the line it is on.
 *
 * `line` is the line number IN THE PERSON'S SPREADSHEET — 0 means the file as a whole.
 * `code` is an ordinary error code and goes through the same catalogue as everything
 * else: a load report reading "ERR_DUP" to an Arabic-speaking administrator is a load
 * report nobody acts on.
 */
export interface LoadProblem {
  line: number
  code: string
  value: string | null
  other: string | null
}

export interface PlannedRoom {
  line: number
  roomNumber: string
  /** Which building. Sprint 48 — see `PlannedBuilding`. */
  building: string
  floor: string
  typeCode: string
  change: LoadChange
  /** Where it is now, when the file would move it. */
  wasFloor: string | null
  wasTypeCode: string | null
  /**
   * THE BUILDING IT IS IN NOW, when the file would move it to another one.
   *
   * Its own field rather than folded into `wasFloor`, because a room moving from tower
   * A's third floor to tower B's third floor is "3 → 3" when the building is left out —
   * a change drawn as no change, which is the one thing this screen must never do.
   */
  wasBuilding: string | null
}

/**
 * A BUILDING THE FILE NAMES. Sprint 48.
 *
 * Derived from the room sheet exactly as floors and types are: the hotel has one list —
 * its rooms — and asking for a second list of buildings produces a file that disagrees
 * with the first. A new building arrives with a code and nothing else and is named after
 * it; the hotel gives it a real name afterwards.
 */
export interface PlannedBuilding {
  code: string
  change: LoadChange
  floors: number
  rooms: number
}

/** A floor the file names, INSIDE a building. A floor code alone does not identify one. */
export interface PlannedFloor {
  code: string
  building: string
  change: LoadChange
  rooms: number
}

export interface PlannedRoomType {
  code: string
  name: string
  nameAr: string | null
  cleanMinutes: number
  change: LoadChange
  rooms: number
  /**
   * HOW MANY PEOPLE A ROOM OF THIS TYPE SLEEPS. Sprint 49.
   *
   * On the preview because it is the number that decides how much linen goes into every
   * room of the type, and the moment to disagree with it is before the load rather than
   * after four hundred rooms have it.
   */
  capacity: number
}

/** The numbers somebody reads before they press the button. */
export interface LoadCounts {
  roomsNew: number
  roomsMoved: number
  roomsUnchanged: number
  roomsLeftOutOfUse: number
  floorsNew: number
  /** Buildings this file would create. Sprint 48. */
  buildingsNew: number
  typesNew: number
  typesChanged: number
  /**
   * ROOMS THE HOTEL HAS THAT THIS FILE DOES NOT MENTION — and they are left alone.
   * The most important number on the screen: a partial spreadsheet would otherwise look
   * like an instruction to close four hundred rooms, and this is how somebody sees that
   * they uploaded the wrong file BEFORE rather than after.
   */
  roomsNotInFile: number
}

export interface LoadPlan {
  linesRead: number
  /** How many rows were written in Eastern Arabic digits and converted to Western ones. */
  convertedFromEasternDigits: number
  problems: LoadProblem[]
  buildings: PlannedBuilding[]
  floors: PlannedFloor[]
  roomTypes: PlannedRoomType[]
  rooms: PlannedRoom[]
  counts: LoadCounts
  /** False while there is a single problem. All or nothing. */
  canCommit: boolean
  /** True on the plan a COMMIT returns — what was actually done. */
  applied: boolean
}

/**
 * THE FILE IS THE BODY, RAW, and its name travels in a header.
 *
 * The same decision as a photograph and for the same reasons: one file per request has
 * nothing to separate from anything else, so multipart would be pure overhead and would
 * make the server buffer the whole thing to parse it. The name is a header rather than a
 * query parameter because a file name is somebody else's text and has no business in a
 * URL, an access log or a browser's history — and here it decides how the file is read.
 */
function send(propertyId: string, file: File, path: string): Promise<LoadPlan> {
  return request<LoadPlan>(`/properties/${propertyId}/rooms/load${path}`, {
    method: 'POST',
    rawBody: file,
    contentType: file.type || 'application/octet-stream',
    headers: { 'X-Housekeeping-File-Name': asciiName(file.name) },
  })
}

export const previewRoomLoad = (propertyId: string, file: File) =>
  send(propertyId, file, '/preview')

export const commitRoomLoad = (propertyId: string, file: File) =>
  send(propertyId, file, '')

/**
 * A HEADER MAY ONLY CARRY LATIN-1, and a hotel in Najaf will upload `الغرف.xlsx`.
 *
 * Anything outside that range would make `fetch` throw before the request left the
 * browser — the upload would fail with a message about invalid characters, for a file
 * whose contents are perfectly fine. Only the EXTENSION is load-bearing here (it decides
 * whether the file is read as a spreadsheet or as CSV), so the rest is replaced rather
 * than being allowed to stop the load.
 */
function asciiName(name: string): string {
  // eslint-disable-next-line no-control-regex
  const stripped = name.replace(/[^ -~]/g, '_')
  return stripped.length > 0 ? stripped : 'upload'
}
